<script setup lang="ts">
import SSLogo from "@/assets/sslogo.svg";
import { FontSetting } from "@/models/FontSetting";
const dark = useDarkModeStore();
const props = withDefaults(
  defineProps<{
    fontSize?: string;
    textAlignment?: string;
    twoColMode?: boolean;
    fontSettings?: {
      headings: FontSetting;
      lyrics: FontSetting;
    };
  }>(),
  {
    fontSettings: () => ({
      headings: {
        size: "16",
        color: "base100",
        highlight: "transparent",
        family: "Sans-Serif",
      },
      lyrics: {
        size: "16",
        color: "base100",
        family: "Sans-Serif",
      },
    }),
  },
);

const profile = useProfileStore();
const store = useSongStore();
const data = computed(() => store.data);
const authors = computed(() =>
  data.value?.authors.map((a) => a.label).join(" | "),
);

const placeholderNotAuthorizeed = computed(
  () =>
    store.placeholderNotLoaded ||
    (store.status === LoadStatus.Ready &&
      !store.song?.products.lyrics.authorized),
);

const emit = defineEmits<{
  (e: "copy"): void;
}>();

const origin = window.location.origin;
function onCopy() {
  if (
    store.status === LoadStatus.Error ||
    store.status === LoadStatus.Loading ||
    !store.song?.lyrics
  ) {
    return;
  }
  emit("copy");
}

const beta = useBetaStore();

const enableFontSettingsFeature = computed(
  () => beta.features.lyricFontSettings,
);

const headingFontFamily = computed(() => {
  const family =
    AvailableFontFamilies.find(
      (f) => f.type === props.fontSettings?.headings.family,
    ) || AvailableFontFamilies[0];
  loadFont(family.id, family.url);
  return family.fontFamilyName;
});

const lyricsFontFamily = computed(() => {
  const family =
    AvailableFontFamilies.find(
      (f) => f.type === props.fontSettings?.lyrics.family,
    ) || AvailableFontFamilies[0];
  loadFont(family.id, family.url);
  return family.fontFamilyName;
});

const headingsColor = computed(() => {
  const color = AvailableTextColors.find(
    (c) => c.name === props.fontSettings?.headings.color,
  );

  if (dark.isDarkModeEnabled) {
    return color?.dark;
  } else {
    return color?.light;
  }
});

const lyricsColor = computed(() => {
  const color = AvailableTextColors.find(
    (c) => c.name === props.fontSettings?.lyrics.color,
  );

  if (dark.isDarkModeEnabled) {
    return color?.dark;
  } else {
    return color?.light;
  }
});

const headingsColorPrint = computed(() => {
  const color = AvailableTextColors.find(
    (c) => c.name === props.fontSettings?.headings.color,
  );

  return color?.light || "black";
});

const lyricsColorPrint = computed(() => {
  const color = AvailableTextColors.find(
    (c) => c.name === props.fontSettings?.lyrics.color,
  );

  return color?.light || "black";
});

const headingsHighlight = computed(() => {
  const color = AvailableHighlightColors.find(
    (c) => c.name === props.fontSettings?.headings.highlight,
  );

  if (dark.isDarkModeEnabled) {
    return color?.dark || "transparent";
  } else {
    return color?.light || "transparent";
  }
});
const headingsHighlightPrint = computed(() => {
  const color = AvailableHighlightColors.find(
    (c) => c.name === props.fontSettings?.headings.highlight,
  );

  return color?.light || "transparent";
});

const lyricsHighlight = computed(() => {
  const color = AvailableHighlightColors.find(
    (c) => c.name === props.fontSettings?.lyrics.highlight,
  );

  if (dark.isDarkModeEnabled) {
    return color?.dark || "transparent";
  } else {
    return color?.light || "transparent";
  }
});

const lyricsHighlightPrint = computed(() => {
  const color = AvailableHighlightColors.find(
    (c) => c.name === props.fontSettings?.lyrics.highlight,
  );

  return color?.light || "transparent";
});

const lyricsFontSize = computed(
  () => `${(parseInt(props.fontSettings.lyrics.size) / 16).toFixed(3)}em`,
);

const headingsFontSize = computed(
  () => `${(parseInt(props.fontSettings.headings.size) / 16).toFixed(3)}em`,
);
</script>

<template>
  <div
    class="music-sheet"
    :class="{ 'enable-font-settings': enableFontSettingsFeature }"
    :style="`font-size: ${fontSize}px; text-align:${textAlignment}`"
    @copy="onCopy"
  >
    <div>
      <header class="song-header">
        <h2 v-if="store.placeholderNotLoaded">
          <span
            class="placeholder"
            style="width: 200px"
          />
        </h2>
        <h2 v-else-if="data?.lyrics">
          {{ data.title }}
        </h2>
      </header>

      <main :class="{ 'two-columns': twoColMode }">
        <div v-if="placeholderNotAuthorizeed">
          <div
            v-for="l of 3"
            :key="l"
            class="lyric-section"
          >
            <h3>
              <span
                class="placeholder"
                style="width: 100px"
              />
            </h3>
            <Placeholder
              :min-width="100"
              :max-width="300"
            />
          </div>
        </div>
        <div v-else-if="data?.lyrics">
          <div
            v-for="(l, i) of data.lyrics"
            :key="i"
            class="lyric-section"
          >
            <h3 class="heading-text">
              <span class="heading-highlight">{{ l.partLabel }}</span>
            </h3>
            <div
              v-for="(ll, ii) of l.lyrics.split('|').map((l) => l.trim())"
              :key="i + 'l' + ii"
              :style="enableFontSettingsFeature && {}"
              class="lyrics-text"
            >
              <span class="lyrics-highlight">{{ ll }}</span>
            </div>
          </div>
        </div>
      </main>

      <footer
        v-if="data?.lyrics"
        class="song-footer"
      >
        <div>
          <img
            :src="SSLogo"
            :alt="$t('SV.LIT_SongSelectLogo')"
            class="img"
          />
        </div>
        <div v-if="!store.placeholderNotLoaded">
          <div>
            {{ $t("G.CcliSongNumber") }}
            <span v-if="store.song?.products.general.authorized">{{
              data.ccliSongNumber
            }}</span
            ><span
              v-else
              class="placeholder"
              style="width: 50px"
            ></span>
          </div>
          <div>{{ authors }}</div>
          <div>{{ data.copyrights }}</div>
          <div>
            <i18n-t
              keypath="SV.MES_SongSelectUseDisclaimer"
              tag="span"
              scope="global"
            >
              <template #terms>
                <a :href="`${origin}/about/termsofuse`">{{
                  $t("G.LIT_TermsOfUse")
                }}</a>
              </template>
              <template #link>
                <a
                  href="https://www.ccli.com"
                  target="_blank"
                  >www.ccli.com</a
                >
              </template>
            </i18n-t>
          </div>
          <div v-if="profile.profile?.licenseNumber">
            {{ $t("G.CcliLicenseNumber") }}
            {{ profile.profile.licenseNumber }}
          </div>
        </div>
        <div v-else>
          <div>
            <span
              class="placeholder"
              style="width: 100px"
            />
          </div>
          <div>
            <span
              class="placeholder"
              style="width: 150px"
            />
          </div>
          <div>
            <span
              class="placeholder"
              style="width: 200px"
            />
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.music-sheet {
  @apply rounded p-5 shadow-lg md:p-10;
}

h2 {
  font-size: 1.75em;
  margin-bottom: 0.75em;
}

h3 {
  @apply font-bold;
  font-size: 1.15em;
}

.lyric-section {
  font-size: 1em;
  padding-bottom: 1em;
  @apply break-inside-avoid;
  print-color-adjust: exact;
}
@media print {
  .lyric-section {
    padding-top: 0.5em;
    padding-bottom: 0.5em;
    margin-bottom: 0em;
  }
}

.img {
  width: 120px;
  height: 24px;
  margin-bottom: 1em;
  @apply inline-block;
}

.song-footer {
  @apply break-inside-avoid pt-5 text-xs;

  a {
    @apply link text-primary;
  }
  & > div {
    @apply m-0;
  }
}

.two-columns {
  columns: 2;

  .lyric-section {
    break-inside: avoid;
  }
}
.highlight {
  print-color-adjust: exact !important;
}

.enable-font-settings {
  .heading-text {
    font-size: v-bind("headingsFontSize");
    font-family: v-bind("headingFontFamily");
  }
  .lyrics-text {
    font-size: v-bind("lyricsFontSize");
    font-family: v-bind("lyricsFontFamily");
  }

  @media screen {
    .heading-text {
      color: v-bind("headingsColor");
    }
    .heading-highlight {
      background-color: v-bind("headingsHighlight");
    }
    .lyrics-text {
      color: v-bind("lyricsColor");
    }
    .lyrics-highlight {
      background-color: v-bind("lyricsHighlight");
    }
  }

  @media print {
    .heading-text {
      color: v-bind("headingsColorPrint");
      print-color-adjust: exact;
    }
    .heading-highlight {
      background-color: v-bind("headingsHighlightPrint");
      print-color-adjust: exact;
    }
    .lyrics-text {
      color: v-bind("lyricsColorPrint");
      print-color-adjust: exact;
    }
    .lyrics-highlight {
      background-color: v-bind("lyricsHighlightPrint");
      print-color-adjust: exact;
    }
  }
}
</style>
